import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { showFullScreenLoading, tryHideFullScreenLoading } from "@/views/components/Loading/fullScreen";
import { checkStatus } from "./helper/checkStatus";
import router from "@/routers";
import type { ApiResponse } from "@/api/interface/apiResponse";
import { showToast } from 'vant'
import { GRAY_VERSION } from '@zhihao/mo-config'

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    loading?: boolean;
    cancel?: boolean;
}

const config: AxiosRequestConfig = {
    // 默认地址请求地址，可在 .env.** 文件中修改
    baseURL: import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL,
    // 设置超时时间
    timeout: 30000,
    // 跨域时候允许携带凭证
    withCredentials: true
};


export class RequestHttp {
    service: AxiosInstance;
    public constructor(config: AxiosRequestConfig) {
        // instantiation
        this.service = axios.create(config);

        /**
         * @description 请求拦截器
         * 客户端发送请求 -> [请求拦截器] -> 服务器
         * token校验(JWT) : 接受服务器返回的 token,存储到 vuex/pinia/本地储存当中
         */
        this.service.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                const userContext = sessionStorage.getItem('zhihao-user')
                let token = ''

                if (userContext) {
                   token = JSON.parse(userContext).token
                }

                config.headers.Version = GRAY_VERSION;
                config.headers["X-platform"] = "h5";

                // 当前请求不需要显示 loading，在 api 服务中通过指定的第三个参数: { loading: false } 来控制
                if (config.loading) {
                    config.loading = true;
                }
                config.loading && showFullScreenLoading();
                if (config.headers && typeof config.headers.set === "function") {
                    if (token) {
                        config.headers.set("Authorization", token);
                    }
                }
                return config;
            },
            (error: AxiosError) => {
                return Promise.reject(error);
            }
        );

        /**
         * @description 响应拦截器
         *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
         */
        this.service.interceptors.response.use(
            async response => {
                const data: ApiResponse<any> = response.data;
                tryHideFullScreenLoading();
                // 登录失效
                if (response.status === 401) {
                    return Promise.reject(data);
                }
                // 全局错误信息拦截（防止下载文件的时候返回数据流，没有 code 直接报错）
                if (data.code && data.code !== "0") {
                    showToast({
                      message: data.message,
                      type: 'fail',
                      position: 'top'
                    });
                    return Promise.reject(data);
                }
                // 成功请求（在页面上除非特殊情况，否则不用处理失败逻辑）
                return data as any;
            },
            async (error: AxiosError) => {
                const { response } = error;
                tryHideFullScreenLoading();
                // 请求超时 && 网络错误单独判断，没有 response
                if (error.message.indexOf("timeout") !== -1) {
                   showToast({
                      icon: 'none',
                      message: "请求超时！请您稍后重试",
                      type: 'fail',
                      position: 'top'
                    });
                };
                if (error.message.indexOf("Network Error") !== -1) {
                   showToast({
                      icon: 'none',
                      message: "网络错误！请您稍后重试",
                      type: 'fail',
                      position: 'top',
                    });
                };
                // 根据服务器响应的错误状态码，做不同的处理
                if (response) checkStatus(response);
                // 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
                if (!window.navigator.onLine) await router.replace("/map");
                return Promise.reject(error);
            }
        );
    }
}

export default new RequestHttp(config);
